/**
 * Every team's `⋯` menu (Figma `315:11169`, `320:11540`; interaction `638:18821`), the same at every
 * level: opens `NewTeam` prefilled with this team as `Dentro de` (absent at the campaign's deepest
 * level -- nothing goes inside it), `TeamAccounts` to add accounts to it, `EditTeam` for its own
 * fields, and a confirmed removal (refused by the api while teams sit inside it). Absent, not
 * disabled, without `control_plane.write`, the same gate `NewTeam` and `Structure` apply to
 * themselves.
 */
import { Ellipsis, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ConfirmDialog } from '../../../chrome/form';
import { IconHint } from '../../../chrome/hint';
import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { useCan } from '../../session/orgContext';
import { t } from '../i18n';
import { EditTeam } from './EditTeam';
import { NewTeam } from './NewTeam';
import { TeamAccounts } from './TeamAccounts';
import { useStructure, useTeamDelete } from './hooks';
import type { TeamOut } from './schemas';

type OpenDialog = 'new' | 'edit' | 'accounts' | 'remove' | null;

export function TeamActions(props: { campaignId: string; team: TeamOut }): ReactNode {
  const { campaignId, team } = props;
  const canWrite = useCan('control_plane.write');
  const structureQuery = useStructure(campaignId);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const removeMutation = useTeamDelete(campaignId);

  if (!canWrite) return null;

  const levels = structureQuery.data?.levels ?? [];
  const isDeepestLevel = levels.every((level) => level.position <= team.level_position);

  return (
    <>
      <DropdownMenu>
        <IconHint label={t('teams.actions.label', { name: team.name })}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon">
              <Ellipsis aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </IconHint>
        <DropdownMenuContent align="end">
          {!isDeepestLevel && (
            <DropdownMenuItem onSelect={() => setDialog('new')}>
              <Plus aria-hidden />
              {t('teams.actions.newTeamIn', { name: team.name })}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog('accounts')}>
            <UserPlus aria-hidden />
            {t('teams.actions.accounts')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('edit')}>
            <Pencil aria-hidden />
            {t('teams.actions.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={() => setDialog('remove')}>
            <Trash2 aria-hidden />
            {t('teams.actions.remove')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewTeam
        campaignId={campaignId}
        initialParentTeamId={team.id}
        open={dialog === 'new'}
        onOpenChange={(next) => setDialog(next ? 'new' : null)}
      />
      <EditTeam
        campaignId={campaignId}
        team={team}
        open={dialog === 'edit'}
        onOpenChange={(next) => setDialog(next ? 'edit' : null)}
      />
      <TeamAccounts
        campaignId={campaignId}
        team={team}
        open={dialog === 'accounts'}
        onOpenChange={(next) => setDialog(next ? 'accounts' : null)}
      />
      <ConfirmDialog
        open={dialog === 'remove'}
        onOpenChange={(next) => {
          setDialog(next ? 'remove' : null);
          if (!next) removeMutation.reset();
        }}
        title={t('teams.remove.title', { name: team.name })}
        description={t('teams.remove.description')}
        confirmLabel={t('teams.remove.confirm')}
        destructive
        pending={removeMutation.isPending}
        failure={removeMutation.error?.failure ?? null}
        onConfirm={() => removeMutation.mutateAsync(team.id)}
      />
    </>
  );
}
